import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ProfileContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTIVE_KEY = 'activeProfileId';

export const useProfiles = () => {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfiles must be used within ProfileProvider');
  return ctx;
};

export const ProfileProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileIdState] = useState(() => localStorage.getItem(ACTIVE_KEY) || null);
  const [loading, setLoading] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setProfiles([]);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/profiles`, { headers });
      setProfiles(res.data || []);
      // Sla actief op of fallback naar eerste
      const savedId = localStorage.getItem(ACTIVE_KEY);
      const found = (res.data || []).find((p) => p.id === savedId);
      if (found) setActiveProfileIdState(found.id);
      else if ((res.data || []).length > 0) {
        setActiveProfileIdState(res.data[0].id);
        localStorage.setItem(ACTIVE_KEY, res.data[0].id);
      } else {
        setActiveProfileIdState(null);
        localStorage.removeItem(ACTIVE_KEY);
      }
    } catch (e) {
      console.error('profiles load fail', e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const setActiveProfileId = useCallback((id) => {
    setActiveProfileIdState(id);
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  }, []);

  const create = useCallback(async (data) => {
    const res = await axios.post(`${API}/profiles`, data, { headers });
    await refresh();
    setActiveProfileId(res.data.id);
    return res.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refresh, setActiveProfileId]);

  const update = useCallback(async (id, data) => {
    const res = await axios.put(`${API}/profiles/${id}`, data, { headers });
    await refresh();
    return res.data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refresh]);

  const remove = useCallback(async (id) => {
    await axios.delete(`${API}/profiles/${id}`, { headers });
    await refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, refresh]);

  const activeProfile = profiles.find((p) => p.id === activeProfileId) || null;

  return (
    <ProfileContext.Provider value={{
      profiles, activeProfile, activeProfileId,
      setActiveProfileId, loading,
      refresh, create, update, remove,
    }}>
      {children}
    </ProfileContext.Provider>
  );
};
